import React from 'react';
import { FaGlobe, FaBuilding, FaStar, FaClock, FaUmbrellaBeach, FaUsers, FaHeart } from 'react-icons/fa';

const CountryCitySelection = ({ 
  countries, 
  cities, 
  activities,
  selectedCountry, 
  selectedCity,
  selectedActivity,
  handleCountrySelect, 
  handleCitySelect,
  handleActivitySelect,
  API_BASE_URL 
}) => {
  return (
    <div className="space-y-10">
      {/* Countries */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FaGlobe className="text-blue-600 text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Select Country</h3>
            <p className="text-sm text-slate-600">Choose your destination country</p>
          </div>
        </div>
        <div className="overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
          <div className="flex gap-4 min-w-max">
            {countries.map((country) => {
              const imageUrl = country.image
                ? country.image.startsWith("http")
                  ? country.image
                  : `${API_BASE_URL.replace("/api/activity", "")}/${country.image.replace(/\\/g, "/")}`
                : null;
              const isSelected = selectedCountry?._id === country._id;

              return (
                <button
                  type="button"
                  key={country._id}
                  onClick={() => handleCountrySelect(country)}
                  className={`flex-shrink-0 w-64 text-left transition-all duration-200 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isSelected ? "ring-2 ring-blue-500 ring-offset-2 shadow-lg" : "hover:shadow-md"}`}
                >
                  <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-blue-300">
                    {imageUrl ? (
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={country.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="text-lg font-bold text-white mb-1">
                            {country.name}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-200">
                              {country.currency} • {country.currencySymbol}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-40 bg-gradient-to-br from-blue-500 to-purple-600 flex flex-col items-center justify-center p-4">
                        <FaGlobe className="text-4xl text-white/90 mb-2" />
                        <h3 className="text-lg font-bold text-white text-center">
                          {country.name}
                        </h3>
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          country.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {country.active ? 'Available' : 'Coming Soon'}
                        </span>
                        <div className="flex items-center text-yellow-500">
                          <FaStar className="w-3 h-3" />
                          <span className="ml-1 text-xs font-medium">4.5</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cities – only if country selected */}
      {selectedCountry && (
        <div className="pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FaBuilding className="text-purple-600 text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Cities in {selectedCountry.name}</h3>
              <p className="text-sm text-slate-600">
                {cities.length > 0 ? `Choose from ${cities.length} cities` : "Loading cities…"}
              </p>
            </div>
          </div>
          {cities.length > 0 ? (
            <div className="overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex gap-4 min-w-max">
                {cities.map((city) => {
                  const imageUrl = city.image
                    ? `${API_BASE_URL.replace("/api/activity", "")}/${city.image.replace(/\\/g, "/")}`
                    : null;
                  const isSelected = selectedCity?._id === city._id;

                  return (
                    <button
                      type="button"
                      key={city._id}
                      onClick={() => handleCitySelect(city)}
                      className={`flex-shrink-0 w-64 text-left rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 ${isSelected ? "ring-2 ring-purple-500 ring-offset-2 shadow-lg" : "hover:shadow-md"}`}
                    >
                      <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-purple-300">
                        {imageUrl ? (
                          <div className="relative h-40 overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={city.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3">
                              <h3 className="text-lg font-bold text-white mb-2">
                                {city.name}
                              </h3>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <FaClock className="text-gray-200 w-3 h-3" />
                                  <span className="text-xs text-gray-200">
                                    {city.timezone}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <FaStar className="text-yellow-400 w-3 h-3" />
                                  <span className="text-xs font-medium text-white">4.7</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-40 bg-gradient-to-br from-purple-500 to-pink-600 flex flex-col items-center justify-center p-4">
                            <FaBuilding className="text-4xl text-white/90 mb-2" />
                            <h3 className="text-lg font-bold text-white text-center">
                              {city.name}
                            </h3>
                            <p className="text-white/80 text-xs mt-1">{city.timezone}</p>
                          </div>
                        )}
                        <div className="p-3">
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              city.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {city.active ? 'Popular' : 'Limited'}
                            </span>
                            <span className="text-xs text-gray-500">
                              50+ activities
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-10 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
              <FaBuilding className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600">No cities available for this country</p>
            </div>
          )}
        </div>
      )}

      {/* Activities – only if city selected */}
      {selectedCity && (
        <div className="pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <FaUmbrellaBeach className="text-emerald-600 text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Activities in {selectedCity.name}</h3>
              <p className="text-sm text-slate-600">
                {activities.length > 0 ? `${activities.length} experiences available` : "Loading activities…"}
              </p>
            </div>
          </div>
          {activities.length > 0 ? (
            <div className="overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex gap-4 min-w-max">
                {activities.map((activity) => {
                  const firstImg = activity.images?.[0];
                  const imageUrl = firstImg
                    ? firstImg.startsWith("http")
                      ? firstImg
                      : `${API_BASE_URL.replace("/api/activity", "")}/${firstImg.replace(/\\/g, "/")}`
                    : null;
                  const isSelected = selectedActivity?._id === activity._id;

                  return (
                    <button
                      type="button"
                      key={activity._id}
                      onClick={() => handleActivitySelect(activity)}
                      className={`flex-shrink-0 w-80 text-left rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 ${isSelected ? "ring-2 ring-emerald-500 ring-offset-2 shadow-lg" : "hover:shadow-md"}`}
                    >
                      <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-emerald-300">
                        {imageUrl ? (
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={activity.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                            <div className="absolute top-3 right-3">
                              <button 
                                className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <FaHeart className="text-gray-600 hover:text-red-500 text-sm" />
                              </button>
                            </div>
                            {activity.images && activity.images.length > 1 && (
                              <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                                <span className="text-xs font-medium text-white">
                                  +{activity.images.length - 1} photos
                                </span>
                              </div>
                            )}
                            <div className="absolute bottom-3 left-3 right-3">
                              <h3 className="text-base font-bold text-white mb-1.5 line-clamp-1">
                                {activity.name}
                              </h3>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center space-x-1">
                                  <FaStar className="text-yellow-400 w-3 h-3" />
                                  <span className="text-xs font-medium text-white">4.8</span>
                                  <span className="text-xs text-gray-300">(124)</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3 text-xs text-gray-200">
                                <div className="flex items-center">
                                  <FaClock className="mr-1 w-2.5 h-2.5" />
                                  <span>{activity.duration} mins</span>
                                </div>
                                <div className="flex items-center">
                                  <FaUsers className="mr-1 w-2.5 h-2.5" />
                                  <span>Max {activity.maxCapacity}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-48 bg-gradient-to-br from-teal-500 to-green-600 flex flex-col items-center justify-center p-4">
                            <FaUmbrellaBeach className="text-4xl text-white/90 mb-2" />
                            <h3 className="text-lg font-bold text-white text-center">
                              {activity.name}
                            </h3>
                          </div>
                        )}
                        <div className="p-4">
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {activity.description || 'Experience the best of ' + selectedCity.name}
                          </p>
                          <div className="flex items-center justify-end">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              activity.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {activity.status === 'active' ? 'Available Now' : 'Limited Slots'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-10 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
              <FaUmbrellaBeach className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600">No activities available for this city</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CountryCitySelection;
